import React, { useMemo, useState } from "react";
import { toSortedArray } from "../../services/workspaceService";

/**
 * Compact workspace path picker:
 * desk → drawer → notebook, optimized for 2–4 taps.
 *
 * Note: it supports "create notebook quick" by setting notebookId="__new__"
 * and emitting newNotebookName via onQuickCreateNotebook.
 */
export default function WorkspacePathPicker({
  workspace,
  deskId,
  drawerId,
  notebookId,
  onChangePath,
  onQuickCreateNotebook,
  newNotebookName,
  style,
}) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("desk"); // desk | drawer | notebook
  const [creatingNotebook, setCreatingNotebook] = useState(false);
  const [draftName, setDraftName] = useState(newNotebookName || "Notas");

  const desks = useMemo(() => toSortedArray(workspace?.desks) || [], [workspace]);
  const selectedDesk = useMemo(() => desks.find((d) => d.id === deskId) || null, [desks, deskId]);
  const drawers = useMemo(() => (selectedDesk ? toSortedArray(selectedDesk.drawers) : []), [selectedDesk]);
  const selectedDrawer = useMemo(() => drawers.find((d) => d.id === drawerId) || null, [drawers, drawerId]);
  const notebooks = useMemo(() => (selectedDrawer ? toSortedArray(selectedDrawer.notebooks) : []), [selectedDrawer]);

  const currentPathLabel = useMemo(() => {
    const dName = selectedDesk?.name || "Destinos";
    const drName = selectedDrawer?.name || "Cajón";
    if (notebookId === "__new__") return `${dName} > ${drName} > (Nuevo)`;
    const nName = notebooks.find((n) => n.id === notebookId)?.name || "Notebook";
    return `${dName} > ${drName} > ${nName}`;
  }, [selectedDesk, selectedDrawer, notebookId, notebooks]);

  const close = () => {
    setOpen(false);
    setCreatingNotebook(false);
    setStep("desk");
  };

  const openPicker = () => {
    setOpen(true);
    setCreatingNotebook(false);
    if (deskId && drawerId) setStep("notebook");
    else if (deskId) setStep("drawer");
    else setStep("desk");
  };

  const selectDesk = (nextDeskId) => {
    onChangePath({ deskId: nextDeskId, drawerId: "", notebookId: "" });
    setStep("drawer");
  };

  const selectDrawer = (nextDrawerId) => {
    onChangePath({ deskId, drawerId: nextDrawerId, notebookId: "" });
    setStep("notebook");
  };

  const selectNotebook = (nextNotebookId) => {
    onChangePath({ deskId, drawerId, notebookId: nextNotebookId });
    close();
  };

  const confirmQuickCreateNotebook = () => {
    const name = (draftName || "").trim() || "Notas";
    onQuickCreateNotebook(name);
    setDraftName(name);
    close();
  };

  return (
    <div style={{ position: "relative", ...style }}>
      <button
        type="button"
        onClick={open ? close : openPicker}
        style={{
          ...chipStyle,
          borderColor: open ? "rgba(212,175,55,0.55)" : "rgba(255,255,255,0.1)",
        }}
        aria-label="Seleccionar destino"
      >
        <span style={{ opacity: 0.9, fontWeight: 600, fontSize: "0.8rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {currentPathLabel}
        </span>
      </button>

      {open && (
        <div style={popoverStyle} onClick={(e) => e.stopPropagation()}>
          <div style={popoverHeaderStyle}>
            <div style={{ fontSize: "0.8rem", color: "#D4AF37", fontWeight: 700 }}>
              {step === "desk" ? "Elegí escritorio" : step === "drawer" ? "Elegí cajón" : "Elegí cuaderno"}
            </div>
            <button type="button" onClick={close} style={miniXStyle} aria-label="Cerrar">
              ×
            </button>
          </div>

          {step === "desk" && (
            <div style={listStyle}>
              {desks.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => selectDesk(d.id)}
                  style={rowBtnStyle}
                >
                  {d.name}
                </button>
              ))}
            </div>
          )}

          {step === "drawer" && (
            <div style={listStyle}>
              {drawers.map((dr) => (
                <button
                  key={dr.id}
                  type="button"
                  onClick={() => selectDrawer(dr.id)}
                  style={rowBtnStyle}
                >
                  {dr.name}
                </button>
              ))}
            </div>
          )}

          {step === "notebook" && (
            <div style={listStyle}>
              {notebooks.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => selectNotebook(n.id)}
                  style={rowBtnStyle}
                >
                  {n.name}
                </button>
              ))}

              <div style={{ height: 8 }} />
              {!creatingNotebook && (
                <button
                  type="button"
                  onClick={() => {
                    setCreatingNotebook(true);
                    setDraftName(newNotebookName || "Notas");
                  }}
                  style={{ ...rowBtnStyle, borderColor: "rgba(212,175,55,0.35)", color: "#D4AF37" }}
                >
                  + Crear notebook rápido
                </button>
              )}

              {creatingNotebook && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <input
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    placeholder="Nombre del notebook"
                    style={inputStyle}
                  />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" onClick={confirmQuickCreateNotebook} style={{ ...rowBtnStyle, flex: 1 }}>
                      Guardar
                    </button>
                    <button type="button" onClick={() => setCreatingNotebook(false)} style={{ ...rowBtnStyle, flex: 1, opacity: 0.7 }}>
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const chipStyle = {
  background: "rgba(255,255,255,0.05)",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,0.1)",
  padding: "10px 12px",
  color: "#fff",
  cursor: "pointer",
  width: "100%",
  maxWidth: 520,
  minWidth: 240,
  textAlign: "left",
};

const popoverStyle = {
  position: "absolute",
  bottom: "90px",
  left: 0,
  right: 0,
  background: "rgba(20,20,20,0.92)",
  border: "1px solid rgba(255,255,255,0.12)",
  backdropFilter: "blur(18px)",
  borderRadius: 14,
  padding: 12,
  zIndex: 1500,
  maxHeight: 420,
  overflow: "auto",
};

const popoverHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 10,
};

const miniXStyle = {
  border: "none",
  background: "transparent",
  color: "rgba(255,255,255,0.7)",
  fontSize: 18,
  cursor: "pointer",
  padding: 2,
};

const listStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const rowBtnStyle = {
  width: "100%",
  textAlign: "left",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 12,
  padding: "10px 12px",
  color: "#fff",
  cursor: "pointer",
  fontSize: "0.9rem",
};

const inputStyle = {
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 12,
  padding: "10px 12px",
  color: "#fff",
  outline: "none",
};

